import React from "react";
import { useI18n } from "@/i18n";

/** Approximate centres of coffee regions (lat, lon). Matched against the product's "Происхождение". */
const ORIGINS: Array<{ keys: string[]; lat: number; lon: number; ru: string; uz: string; en: string }> = [
  { keys: ["эфиоп", "ethiop", "efiop"], lat: 8, lon: 39, ru: "Эфиопия", uz: "Efiopiya", en: "Ethiopia" },
  { keys: ["кени", "kenya", "keniya"], lat: 0, lon: 37.5, ru: "Кения", uz: "Keniya", en: "Kenya" },
  { keys: ["руанд", "rwanda", "ruanda"], lat: -2, lon: 30, ru: "Руанда", uz: "Ruanda", en: "Rwanda" },
  { keys: ["бурунд", "burundi"], lat: -3.4, lon: 29.9, ru: "Бурунди", uz: "Burundi", en: "Burundi" },
  { keys: ["уганд", "uganda"], lat: 1.4, lon: 32.3, ru: "Уганда", uz: "Uganda", en: "Uganda" },
  { keys: ["танзан", "tanzan"], lat: -6.4, lon: 34.9, ru: "Танзания", uz: "Tanzaniya", en: "Tanzania" },
  { keys: ["йемен", "yemen", "yaman"], lat: 15.5, lon: 44, ru: "Йемен", uz: "Yaman", en: "Yemen" },
  { keys: ["бразил", "brazil", "braziliya"], lat: -15, lon: -47, ru: "Бразилия", uz: "Braziliya", en: "Brazil" },
  { keys: ["колумб", "colomb", "kolumb"], lat: 4.5, lon: -74, ru: "Колумбия", uz: "Kolumbiya", en: "Colombia" },
  { keys: ["перу", "peru"], lat: -9, lon: -75, ru: "Перу", uz: "Peru", en: "Peru" },
  { keys: ["боливи", "bolivia"], lat: -16.5, lon: -64.5, ru: "Боливия", uz: "Boliviya", en: "Bolivia" },
  { keys: ["эквадор", "ecuador", "ekvador"], lat: -1.8, lon: -78, ru: "Эквадор", uz: "Ekvador", en: "Ecuador" },
  { keys: ["гватемал", "guatemal", "gvatemal"], lat: 15.5, lon: -90.3, ru: "Гватемала", uz: "Gvatemala", en: "Guatemala" },
  { keys: ["гондурас", "hondur", "gondur"], lat: 15, lon: -86.5, ru: "Гондурас", uz: "Gonduras", en: "Honduras" },
  { keys: ["коста", "costa", "kosta"], lat: 9.8, lon: -84, ru: "Коста-Рика", uz: "Kosta-Rika", en: "Costa Rica" },
  { keys: ["сальвадор", "salvador"], lat: 13.8, lon: -88.9, ru: "Сальвадор", uz: "Salvador", en: "El Salvador" },
  { keys: ["никараг", "nicarag", "nikarag"], lat: 12.9, lon: -85.2, ru: "Никарагуа", uz: "Nikaragua", en: "Nicaragua" },
  { keys: ["панам", "panam"], lat: 8.5, lon: -80.8, ru: "Панама", uz: "Panama", en: "Panama" },
  { keys: ["мексик", "mexic", "meksik"], lat: 17, lon: -96.5, ru: "Мексика", uz: "Meksika", en: "Mexico" },
  { keys: ["индонез", "indonez", "indones", "суматр", "sumatra"], lat: -2, lon: 110, ru: "Индонезия", uz: "Indoneziya", en: "Indonesia" },
  { keys: ["вьетнам", "vietnam", "vyetnam"], lat: 12.5, lon: 108, ru: "Вьетнам", uz: "Vyetnam", en: "Vietnam" },
  { keys: ["инди", "india", "hindiston"], lat: 12.5, lon: 76, ru: "Индия", uz: "Hindiston", en: "India" },
  { keys: ["папуа", "papua"], lat: -6, lon: 145, ru: "Папуа — Новая Гвинея", uz: "Papua — Yangi Gvineya", en: "Papua New Guinea" },
  { keys: ["китай", "china", "xitoy", "юньнань", "yunnan"], lat: 23, lon: 101, ru: "Китай", uz: "Xitoy", en: "China" },
];

/** All countries known to the map (for the coffee passport). */
export const ALL_ORIGINS = ORIGINS;

export function findOrigin(text: string | undefined) {
  if (!text) return null;
  const low = text.toLowerCase();
  return ORIGINS.find((o) => o.keys.some((k) => low.includes(k))) ?? null;
}

/**
 * A quiet "where it grows" map: graticule, the coffee belt between the
 * tropics, and a marker at the origin. Shown only when the origin is known.
 */
export function OriginMap({ origin, region }: { origin: NonNullable<ReturnType<typeof findOrigin>>; region?: string }) {
  const { t, locale } = useI18n();
  const W = 300;
  const H = 150;
  const x = ((origin.lon + 180) / 360) * W;
  const y = ((90 - origin.lat) / 180) * H;
  const tropic = (23.4 / 180) * H;
  return (
    <div className="origin-map">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={origin[locale]}>
        {[30, 60, 90, 120].map((gy) => (
          <line key={gy} x1="0" x2={W} y1={gy} y2={gy} className="origin-map__grid" />
        ))}
        {Array.from({ length: 11 }, (_, i) => (i + 1) * (W / 12)).map((gx) => (
          <line key={gx} y1="0" y2={H} x1={gx} x2={gx} className="origin-map__grid" />
        ))}
        <rect x="0" y={H / 2 - tropic} width={W} height={tropic * 2} className="origin-map__belt" />
        <line x1="0" x2={W} y1={H / 2} y2={H / 2} className="origin-map__equator" />
        <circle cx={x} cy={y} r="11" className="origin-map__pulse" />
        <rect x={x - 5} y={y - 5} width="10" height="10" transform={`rotate(45 ${x} ${y})`} className="origin-map__pin" />
      </svg>
      <div className="origin-map__caption">
        <span className="eyebrow">{t("product.originTitle")}</span>
        <strong>{origin[locale]}</strong>
        {region && <small>{region}</small>}
        <small className="origin-map__belt-note">{t("product.coffeeBelt")}</small>
      </div>
    </div>
  );
}
